import { useContext, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DeviceService } from '@data';
import { Status } from '@general';
import { yupResolver } from '@hookform/resolvers/yup';
import { DeviceSummary, FaultyDeviceRequestBody, ResourceSourceType } from '@types';
import { Resolver, useForm } from 'react-hook-form';

import {
	ButtonQHDS,
	CheckboxGroupFieldQldDS,
	ControllerPlus,
	DateFieldQldDS,
	RadioGroupFieldQldDS,
	SelectFieldQldDS,
	TextFieldQldDS,
} from '@/components';
import { CONSTANT } from '@/constants';
import { LegalEntityContext, ModalContext } from '@/context';
import {
	hasServerErrorInForm,
	hasServerResponseCodeInForm,
	isValidUUID,
	mapDevicesToOptions,
	safeId,
	setServerErrorInForm,
	useHideAlertOnChange,
} from '@/utils';
import { affirmationOptions } from '@/utils/moduleAForm';
import {
	defaultDeviceFaultyFormValues,
	deviceFaultyFormLabels,
	deviceFaultyFormSchema,
	DeviceFaultyFormValues,
	faultReasonOptions,
} from '@/utils/reportForm';

import styles from './deviceFaultyForm.module.scss';

/**
 * Interface for props
 */
interface DeviceFaultyFormProps {
	/** Callback when the form is success */
	handleSuccess?: () => void;
}

export const DeviceFaultyForm = ({
	handleSuccess = () => {
		return;
	},
}: DeviceFaultyFormProps) => {
	// Context
	const { legalEntityList } = useContext(LegalEntityContext);
	// State
	const [selectedDevice, setSelectedDevice] = useState<DeviceSummary | undefined>();
	const [showError, setShowError] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);
	const { setShowDeviceFaultyFormModal } = useContext(ModalContext);

	// Form
	const form = useForm<DeviceFaultyFormValues>({
		resolver: yupResolver(deviceFaultyFormSchema) as unknown as Resolver<DeviceFaultyFormValues>,
		defaultValues: defaultDeviceFaultyFormValues,
	});

	const {
		control,
		handleSubmit,
		watch,
		formState: { errors, isSubmitting },
	} = form;

	const formValues = watch();
	const deviceId = watch('deviceID');
	const hasOtherPersons = watch('hasOtherPersons');
	const isDQPKnown = watch('isDQPKnown');
	const router = useRouter();

	// Data
	const getAllDevices = useQuery({
		queryKey: [CONSTANT.DEVICES_API_URL, 'all'],
		queryFn: () => DeviceService.getAllDevices(legalEntityList),
		enabled: legalEntityList.length > 0,
	});

	const reportDeviceAsFaulty = useMutation({
		mutationFn: (reportIssueCreation: FaultyDeviceRequestBody) =>
			DeviceService.reportDeviceAsFaulty(
				safeId(selectedDevice?.clientId),
				safeId(selectedDevice?.id),
				reportIssueCreation,
			),
	});

	// Memoized options and values
	const devicesOptions = useMemo(
		() => mapDevicesToOptions(getAllDevices.data ?? []),
		[getAllDevices.data],
	);

	// Life cycle
	/**
	 * Hide alert when form values change
	 */
	useHideAlertOnChange(form, setShowError);

	/**
	 * Sets selected device data when the dropdown value (deviceId) changes
	 * It checks if the device ID is a valid UUID, and if so, it finds the device in the list of all devices.
	 * If the device is found, it sets the selected device to that device.
	 */
	useEffect(() => {
		if (!isValidUUID(deviceId)) return;
		const device = getAllDevices.data?.find(item => item.id === deviceId);
		setSelectedDevice(device);
	}, [deviceId, getAllDevices.data]);

	// Helpers
	const onSubmit = async (values: DeviceFaultyFormValues) => {
		const data = castFormValues(values);
		await reportDeviceAsFaulty
			.mutateAsync(data)
			.then(() => {
				setShowSuccess(true);
				handleSuccessWithDelay();
			})
			.catch(e => setServerErrorInForm(form, e)); // set server error if delete or create fails
	};

	const onInvalid = () => {
		setShowError(true);
	};

	const castFormValues = (values: DeviceFaultyFormValues): FaultyDeviceRequestBody => {
		return {
			faultIdentifiedDate: values.faultIdentifiedDate.toISOString(),
			faultReason: values.faultReason,
			relevantAuthorisations: values.relevantAuthorisations,
			personDetails: values.personDetails,
			dqpName: values.dqpName,
			comment: values.comment,
			sourceType: ResourceSourceType.WEB,
		};
	};

	const handleSuccessWithDelay = () => {
		const timer = setTimeout(() => {
			handleSuccess();
		}, 1500);
		return () => clearTimeout(timer);
	};

	function getStatusMessage(): React.ReactNode {
		if (showSuccess) {
			return 'Issue reported';
		} else if (hasServerErrorInForm(errors)) {
			if (hasServerResponseCodeInForm(errors, '409')) {
				return (
					<>
						<b>Already in Progress</b>
						<br></br>
						Looks like this device already has a certification in progress.
						<br></br>
						Need help?&nbsp;
						<Link
							href="#"
							onClick={() => {
								setShowDeviceFaultyFormModal(false);
								router.push('/contact');
							}}
							className={styles.help}
						>
							Contact us
						</Link>
					</>
				);
			} else {
				return 'Report has failed due to server issue. Please try again later.';
			}
		} else {
			return 'Please resolve the errors above.';
		}
	}

	// Layout
	return (
		<div className={styles.container}>
			<form
				onSubmit={handleSubmit(onSubmit, onInvalid)}
				className={styles.form}
				style={{ WebkitOverflowScrolling: 'touch' }} // Ensure iOS Safari handles scrolling properly
			>
				<ControllerPlus<DeviceFaultyFormValues>
					control={control}
					name="deviceID"
					schema={deviceFaultyFormSchema}
					labels={deviceFaultyFormLabels}
					disabled={isSubmitting}
					formValues={formValues}
				>
					<SelectFieldQldDS
						options={devicesOptions}
						placeholder="Select a device"
						errorMsg={errors.deviceID?.message}
						isLoading={getAllDevices.isLoading}
					/>
				</ControllerPlus>
				<ControllerPlus<DeviceFaultyFormValues>
					control={control}
					name="faultIdentifiedDate"
					schema={deviceFaultyFormSchema}
					labels={deviceFaultyFormLabels}
					disabled={isSubmitting}
					formValues={formValues}
				>
					<DateFieldQldDS size="large" errorMsg={errors.faultIdentifiedDate?.message} />
				</ControllerPlus>
				<ControllerPlus<DeviceFaultyFormValues>
					control={control}
					name="faultReason"
					schema={deviceFaultyFormSchema}
					labels={deviceFaultyFormLabels}
					disabled={isSubmitting}
					formValues={formValues}
				>
					<CheckboxGroupFieldQldDS
						fieldName="faultReason"
						options={faultReasonOptions}
						errorMsg={errors.faultReason?.message}
					/>
				</ControllerPlus>
				<ControllerPlus<DeviceFaultyFormValues>
					control={control}
					name="relevantAuthorisations"
					schema={deviceFaultyFormSchema}
					labels={deviceFaultyFormLabels}
					disabled={isSubmitting}
					formValues={formValues}
				>
					<TextFieldQldDS type="textarea" errorMsg={errors.relevantAuthorisations?.message} />
				</ControllerPlus>
				<ControllerPlus<DeviceFaultyFormValues>
					control={control}
					name="hasOtherPersons"
					schema={deviceFaultyFormSchema}
					labels={deviceFaultyFormLabels}
					disabled={isSubmitting}
					formValues={formValues}
				>
					<RadioGroupFieldQldDS
						fieldName="hasOtherPersons"
						options={affirmationOptions}
						errorMsg={errors.hasOtherPersons?.message}
					/>
				</ControllerPlus>
				{hasOtherPersons && (
					<ControllerPlus<DeviceFaultyFormValues>
						control={control}
						name="personDetails"
						schema={deviceFaultyFormSchema}
						labels={deviceFaultyFormLabels}
						disabled={isSubmitting}
						formValues={formValues}
					>
						<TextFieldQldDS type="textarea" errorMsg={errors.personDetails?.message} />
					</ControllerPlus>
				)}
				<ControllerPlus<DeviceFaultyFormValues>
					control={control}
					name="isDQPKnown"
					schema={deviceFaultyFormSchema}
					labels={deviceFaultyFormLabels}
					disabled={isSubmitting}
					formValues={formValues}
				>
					<RadioGroupFieldQldDS
						fieldName="isDQPKnown"
						options={affirmationOptions}
						errorMsg={errors.isDQPKnown?.message}
					/>
				</ControllerPlus>
				{isDQPKnown && (
					<ControllerPlus<DeviceFaultyFormValues>
						control={control}
						name="dqpName"
						schema={deviceFaultyFormSchema}
						labels={deviceFaultyFormLabels}
						disabled={isSubmitting}
						formValues={formValues}
					>
						<TextFieldQldDS type="textarea" errorMsg={errors.dqpName?.message} />
					</ControllerPlus>
				)}
				<ControllerPlus<DeviceFaultyFormValues>
					control={control}
					name="comment"
					schema={deviceFaultyFormSchema}
					labels={deviceFaultyFormLabels}
					disabled={isSubmitting}
					formValues={formValues}
				>
					<TextFieldQldDS type="textarea" errorMsg={errors.comment?.message} />
				</ControllerPlus>
				<div className={styles.status}>
					<Status
						status={showSuccess ? 'success' : 'error'}
						show={!!showError || showSuccess || hasServerErrorInForm(errors)}
						onClose={() => {
							setShowError(false);
							setShowSuccess(false);
							form.clearErrors();
						}}
						msg={getStatusMessage()}
						largeArea={hasServerResponseCodeInForm(errors, '409')}
					/>
				</div>
				<div className={styles['buttons-container']}>
					<ButtonQHDS
						type="primary"
						size="small"
						onClick={handleSubmit(onSubmit, onInvalid)}
						isLoading={isSubmitting}
						disabled={reportDeviceAsFaulty.isSuccess}
					>
						Submit
					</ButtonQHDS>
				</div>
			</form>
		</div>
	);
};
